// hooks/use-chat-lifecycle.js
// ═══════════════════════════════════════════════════════════
// Encapsulates the three useChatThread lifecycle callbacks for the
// main chat feature: beforeSend, buildRequest, onResponseDone.
//
// Keeps chat-feature.js focused on layout orchestration.
// All behaviour is identical to what was inline — pure extraction.
// ═══════════════════════════════════════════════════════════

import { useRef } from 'react';
import { useArtifacts } from './use-artifacts';

export function useChatLifecycle({
  userId,
  supabase,
  currentSessionId,
  userRecord,
  isIncognito,
  pendingFile,
  setPendingFile,
  deepSearchActive,
  setDeepSearchActive,
  setThreadHistory,
}) {
  const { saveArtifact, detectArtifact } = useArtifacts({ userId, supabase });

  // Carries values across the three lifecycle callbacks within a single send.
  const sendTransitRef = useRef({ wasDeepSearch: false, fileToSend: null, filePublicUrl: null });

  async function beforeSend() {
    const fileToSend = pendingFile;
    setPendingFile(null);
    const wasDeepSearch = deepSearchActive;
    if (wasDeepSearch) setDeepSearchActive(false);
    sendTransitRef.current = { wasDeepSearch, fileToSend, filePublicUrl: null };
    if (!fileToSend) return { fileData: null };
    try {
      const { data: { session: authS } } = await supabase.auth.getSession();
      const attachRes = await fetch('/api/user/save-attachment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authS?.access_token || ''}` },
        body: JSON.stringify({
          sessionId: currentSessionId,
          file: { base64: fileToSend.base64, name: fileToSend.name, type: fileToSend.type, isImage: fileToSend.isImage },
        }),
      });
      if (attachRes.ok) {
        const { url } = await attachRes.json();
        sendTransitRef.current.filePublicUrl = url || null;
        if (url) saveArtifact(url, 'Upload', currentSessionId).catch(() => {});
      }
    } catch { /* non-critical — file upload failed, still send message */ }
    return { fileData: fileToSend };
  }

  async function buildRequest({ text, fileData, messagesSnapshot, signal }) {
    const { data: { session: authSession } } = await supabase.auth.getSession();
    const firstUserIdx = messagesSnapshot.findIndex(m => m.role === 'user');
    const currentMessages = firstUserIdx > 0 ? messagesSnapshot.slice(firstUserIdx) : messagesSnapshot;
    const cleanMessages = currentMessages.map((m) => {
      if (Array.isArray(m.content)) {
        return {
          role: m.role,
          content: m.content.map(block =>
            block.type === 'image' || block.type === 'document'
              ? { type: 'text', text: '[File was shared earlier in this conversation]' }
              : block
          ),
        };
      }
      return { role: m.role, content: m.content };
    });
    const { wasDeepSearch, filePublicUrl } = sendTransitRef.current;
    const requestBody = {
      messages: cleanMessages,
      userId,
      sessionId: currentSessionId,
      aiName: userRecord?.ai_name || 'Shift',
      userName: userRecord?.first_name || null,
      ...(isIncognito ? { silent: true } : {}),
      ...(wasDeepSearch ? { action: 'deepSearch', query: text } : {}),
      ...(fileData ? {
        fileData: {
          base64: fileData.base64, name: fileData.name, type: fileData.type,
          isImage: fileData.isImage, isPDF: fileData.isPDF, publicUrl: filePublicUrl,
        },
      } : {}),
    };
    return {
      url: '/api/chat',
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authSession?.access_token || ''}` },
      body: requestBody,
      signal,
    };
  }

  async function onResponseDone(finalText, _userMsg, metadata) {
    const detected = detectArtifact(finalText);
    if (detected) saveArtifact(detected.content, detected.type, currentSessionId, { messageId: metadata?.assistantMsgId });
    const { fileToSend, filePublicUrl } = sendTransitRef.current;
    if (fileToSend && filePublicUrl && finalText.length > 20) {
      try {
        const { data: { session: authS } } = await supabase.auth.getSession();
        const descRes = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authS?.access_token || ''}` },
          body: JSON.stringify({
            action: 'generateArtifactDescription',
            content: finalText.slice(0, 2000),
            title: fileToSend.name || 'Upload',
            type: 'upload',
            userId,
          }),
        });
        const descData = await descRes.json();
        if (descData.description || descData.bullets?.length || descData.title) {
          await fetch('/api/user/update-artifact-desc', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authS?.access_token || ''}` },
            body: JSON.stringify({ url: filePublicUrl, title: descData.title, description: descData.description, bullets: descData.bullets }),
          });
        }
      } catch { /* non-critical */ }
    }
    sendTransitRef.current = { wasDeepSearch: false, fileToSend: null, filePublicUrl: null };
    setTimeout(async () => {
      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        const r = await fetch('/api/user/threads', { headers: { 'Authorization': `Bearer ${s?.access_token || ''}` } });
        if (r.ok) { const { threads } = await r.json(); if (threads) setThreadHistory(threads); }
      } catch { /* non-fatal */ }
    }, 1500);
  }

  return { beforeSend, buildRequest, onResponseDone };
}
